// Filtering, searching and sorting for the "My Active Numbers" inventory (pure, no I/O).
import { US_STATES } from './usConstants.js';

export const DEFAULT_FILTERS = Object.freeze({ search: '', state: 'all', type: 'all', range: 'all', from: '', to: '', sort: 'newest' });

export const TYPE_OPTIONS = Object.freeze([
  { value: 'all', label: 'All Types' },
  { value: 'default', label: 'Default' },
  { value: 'special', label: 'Special Number' },
  { value: 'standard', label: 'Standard' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
]);
export const RANGE_OPTIONS = Object.freeze([
  { value: 'all', label: 'All Time' },
  { value: '7', label: 'Last 7 Days' },
  { value: '30', label: 'Last 30 Days' },
  { value: '90', label: 'Last 90 Days' },
  { value: 'custom', label: 'Custom range…' },
]);
export const SORT_OPTIONS = Object.freeze([
  { value: 'newest', label: 'Date Added: Newest First' },
  { value: 'oldest', label: 'Date Added: Oldest First' },
  { value: 'state', label: 'State / Area Code: A-Z' },
  { value: 'number-asc', label: 'Phone Number: Ascending' },
  { value: 'number-desc', label: 'Phone Number: Descending' },
  { value: 'priority', label: 'Status: Default & Special First' },
]);

const NAME_TO_CODE = new Map(US_STATES.map((s) => [s.name.toLowerCase(), s.code]));
const CODE_TO_NAME = new Map(US_STATES.map((s) => [s.code, s.name]));
const digits = (v) => String(v || '').replace(/\D/g, '');
const time = (n) => (n?.createdAt ? new Date(n.createdAt).getTime() || 0 : 0);

/** Two-letter state for a number (stored region/state, accepting full names), or ''. */
export function stateOf(n) {
  const raw = String(n?.region || n?.state || '').trim();
  if (!raw) return '';
  const up = raw.toUpperCase();
  if (CODE_TO_NAME.has(up)) return up;
  return NAME_TO_CODE.get(raw.toLowerCase()) || '';
}

/** 'Texas' for 'TX'; falls back to the code. */
export const stateName = (code) => CODE_TO_NAME.get(code) || code;

/** States present in the inventory (with counts), A-Z by name; numbers with no known state are counted separately. */
export function stateOptions(numbers = []) {
  const counts = new Map();
  let unknown = 0;
  for (const n of numbers) {
    const s = stateOf(n);
    if (s) counts.set(s, (counts.get(s) || 0) + 1); else unknown += 1;
  }
  const list = [...counts].map(([code, count]) => ({ code, name: stateName(code), count }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return { list, unknown };
}

/** @returns {boolean} Does `n` belong to the chosen role/status filter? */
export function matchesType(n, type) {
  const inactive = n?.status === 'inactive';
  switch (type) {
    case 'default': return !!n?.isDefault;
    case 'special': return !!n?.isSpecial;
    case 'standard': return !inactive && !n?.isDefault && !n?.isSpecial;
    case 'active': return !inactive;
    case 'inactive': return inactive;
    default: return true;
  }
}

function matchesRange(n, { range, from, to }, now) {
  if (range === 'all') return true;
  const t = time(n);
  if (!t) return false; // no purchase date: cannot be placed in a date window
  if (range === 'custom') {
    const lo = from ? new Date(`${from}T00:00:00`).getTime() : -Infinity;
    const hi = to ? new Date(`${to}T23:59:59.999`).getTime() : Infinity;
    return t >= lo && t <= hi;
  }
  return t >= now - Number(range) * 86400000;
}

function matchesSearch(n, q) {
  const text = String(q || '').trim().toLowerCase();
  if (!text) return true;
  const qd = digits(text);
  if (qd && text.replace(/[\d\s()+.-]/g, '') === '') {
    const nd = digits(n?.number);
    return nd.includes(qd) || (qd.length === 11 && qd.startsWith('1') && nd.includes(qd.slice(1)));
  }
  const hay = [n?.city, n?.region, n?.state, stateName(stateOf(n)), n?.areaCode].filter(Boolean).join(' ').toLowerCase();
  return hay.includes(text) || digits(n?.number).includes(qd && qd.length > 2 ? qd : '\u0000');
}

function priority(n) {
  if (n?.isDefault) return 0;
  if (n?.isSpecial) return 1;
  return n?.status === 'inactive' ? 3 : 2;
}

const byNumber = (a, b) => digits(a.number).localeCompare(digits(b.number));

/** Sort comparator for a SORT_OPTIONS value. Ties fall back to newest first, then number, so order is stable. */
export function comparator(sort) {
  const tie = (a, b) => time(b) - time(a) || byNumber(a, b);
  switch (sort) {
    case 'oldest': return (a, b) => time(a) - time(b) || byNumber(a, b);
    case 'state': return (a, b) => {
      const sa = stateOf(a); const sb = stateOf(b);
      if (!sa !== !sb) return sa ? -1 : 1; // numbers with no known state go last
      return stateName(sa).localeCompare(stateName(sb))
        || String(a.areaCode || '').localeCompare(String(b.areaCode || '')) || byNumber(a, b);
    };
    case 'number-asc': return byNumber;
    case 'number-desc': return (a, b) => byNumber(b, a);
    case 'priority': return (a, b) => priority(a) - priority(b) || tie(a, b);
    default: return tie; // newest
  }
}

/**
 * Filter then sort. Returns a NEW array of the same objects (never mutates `numbers`), so each row keeps its `_id`.
 * @param {object[]} numbers
 * @param {Partial<typeof DEFAULT_FILTERS>} f
 * @param {number} [now] Epoch ms (injectable for tests).
 */
export function applyFilters(numbers = [], f = DEFAULT_FILTERS, now = Date.now()) {
  const flt = { ...DEFAULT_FILTERS, ...f };
  return numbers
    .filter((n) => (flt.state === 'all' || (flt.state === 'none' ? !stateOf(n) : stateOf(n) === flt.state))
      && matchesType(n, flt.type) && matchesRange(n, flt, now) && matchesSearch(n, flt.search))
    .sort(comparator(flt.sort));
}

/** @returns {number} How many filters are narrowing the list (sorting does not count). */
export function activeFilterCount(f) {
  return activePills(f).length;
}

/** Removable "pill" descriptions of the filters currently applied. */
export function activePills(f = DEFAULT_FILTERS) {
  const flt = { ...DEFAULT_FILTERS, ...f };
  const pills = [];
  if (flt.search.trim()) pills.push({ key: 'search', label: `Search: “${flt.search.trim()}”` });
  if (flt.state !== 'all') pills.push({ key: 'state', label: flt.state === 'none' ? 'State: Unknown' : `State: ${stateName(flt.state)}` });
  if (flt.type !== 'all') pills.push({ key: 'type', label: `Type: ${TYPE_OPTIONS.find((o) => o.value === flt.type)?.label || flt.type}` });
  if (flt.range === 'custom') {
    if (flt.from || flt.to) pills.push({ key: 'range', label: `Added: ${flt.from || '…'} → ${flt.to || '…'}` });
  } else if (flt.range !== 'all') {
    pills.push({ key: 'range', label: `Added: ${RANGE_OPTIONS.find((o) => o.value === flt.range)?.label || flt.range}` });
  }
  return pills;
}

/** Filters with one pill removed (sort is kept). */
export function clearPill(f, key) {
  const reset = { search: { search: '' }, state: { state: 'all' }, type: { type: 'all' }, range: { range: 'all', from: '', to: '' } }[key] || {};
  return { ...f, ...reset };
}

/** Clear every filter but keep the chosen sort order. */
export const clearAll = (f) => ({ ...DEFAULT_FILTERS, sort: f?.sort || DEFAULT_FILTERS.sort });
